import React, { useState, useContext, useEffect } from 'react';
import { StyleSheet, ScrollView, View, Text, FlatList, StatusBar, RefreshControl, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PaymentSummaryProvider, PaymentSummaryContext } from '@/features/payments/context/PaymentSummaryContext';
import { ChargeSummaryLayout } from '@/features/payments/components/ChargeSummaryLayout';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';
import { MobileSegmentedControl } from '@/components/common/residents/mobile/MobileSegmentedControl';
import { MobilePaymentCard } from '@/features/payments/components/mobile/MobilePaymentCard';
import { MobilePaymentCardLightSkeleton } from '@/features/payments/components/mobile/MobilePaymentCardLightSkeleton';
import { MobilePaymentHistoryTab } from '@/features/payments/components/mobile/MobilePaymentHistoryTab';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery, useGetLeaseEntrataDisabledQuery, useGetLeaseBalanceQuery } from '@/store/api';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { strings } from '@/constants/residents/strings';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

const MobilePaymentsContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useAppDispatch();
  const entrataDisabled = useEntrataIntegrationDisabled();
  const user = useAppSelector((s) => s.auth.user as any);
  
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch linked accounts on mount
  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // // Show disabled page if Entrata is disabled
  // if (entrataDisabled) {
  //   return <UndergoingMaintenancePage variant="disabled" />;
  // }

  // // Show future lease messaging if user's lease hasn't started yet
  // if (user?.hasFutureLease) {
  //   return <UndergoingMaintenancePage variant="future" />;
  // }

  const allLeaseIds = [...ownLeaseIds, ...connectionLeaseIds];
  const hasLeases = allLeaseIds.length > 0;

  const renderEmptyState = () => (
    <View style={styles.mobileEmptyState}>
      <Text style={styles.mobileEmptyStateTitle}>
        {strings.mobilePayments.emptyCurrentTitle}
      </Text>
      <Text style={styles.mobileEmptyStateMessage}>
        {strings.mobilePayments.emptyCurrentMessage}
      </Text>
    </View>
  );

  return (
    <View style={styles.mobileContainer}>
      <StatusBar barStyle="dark-content" backgroundColor={RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY} />
      
      {/* Header Title */}
      <SafeAreaView edges={['top']} style={styles.mobileHeaderSafeArea}>
        <View style={styles.mobileHeaderTitleRow}>
          <Text style={styles.mobileHeaderTitle}>{strings.mobilePayments.title}</Text>
        </View>
      </SafeAreaView>

      {/* Segmented Control */}
      <MobileSegmentedControl
        tabs={[strings.mobilePayments.tabCurrent, strings.mobilePayments.tabHistory]}
        selectedIndex={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Content */}
      {isLoading && !hasLeases ? (
        <LoadingSpinner message="Loading payments..." variant="admin" />
      ) : activeTab === 0 ? (
        // Current Tab - Show all leases with payment cards
        <FlatList
          data={allLeaseIds}
          keyExtractor={(item) => `lease-${item}`}
          renderItem={({ item: leaseId }) => (
            <LeasePaymentCard leaseId={leaseId} isConnection={connectionLeaseIds.includes(leaseId)} />
          )}
          contentContainerStyle={styles.mobileListContent}
          ListEmptyComponent={renderEmptyState}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY}
            />
          }
          showsVerticalScrollIndicator={false}
        />
      ) : (
        // History Tab
        <MobilePaymentHistoryTab />
      )}
    </View>
  );
};

// Wrapper component to fetch and display individual lease data
const LeasePaymentCard: React.FC<{ leaseId: number; isConnection: boolean }> = ({ leaseId, isConnection }) => {
  const entrataDisabled = useEntrataIntegrationDisabled();
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const { data: balanceData } = useGetLeaseBalanceQuery(leaseId, { skip: !leaseId });

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId || entrataDisabled === true }
  );

  const leaseEntrataDisabledQuery = useGetLeaseEntrataDisabledQuery(
    { leaseId },
    { skip: !leaseId || entrataDisabled === false }
  );

  const isFetching = entrataDisabled ? leaseEntrataDisabledQuery.isFetching : leaseQuery.isFetching;
  const isError = entrataDisabled ? leaseEntrataDisabledQuery.isError : leaseQuery.isError;
  const isSuccess = entrataDisabled ? leaseEntrataDisabledQuery.isSuccess : leaseQuery.isSuccess;
  const data = entrataDisabled ? leaseEntrataDisabledQuery.data : leaseQuery.data;

  if (isFetching) {
    return <MobilePaymentCardLightSkeleton />;
  }

  if (isError || !isSuccess || !data) {
    return null;
  }

  const response = (data as any)?.response || data;
  const lease = response?.lease || response?.lease?.id ? response.lease : response;
  const property = lease?.property || {};
  const users = lease?.users || [];
  const paymentsBlocked = !!lease?.paymentsBlocked;
  const autopayEnabled = Array.isArray(lease?.recurringPayments) && lease.recurringPayments.length > 0;
  const balanceDue = Number(balanceData?.balanceDue || 0);
  const unitNumberSpace = lease?.unitNumberSpace || lease?.unitNumber || '';

  // Find the display user (not the current user)
  const connectedPrimary = users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {};

  const residentName = displayUser?.firstName && displayUser?.lastName
    ? `${displayUser.firstName} ${displayUser.lastName}`
    : 'Resident';

  return (
    <MobilePaymentCard
      leaseId={leaseId}
      residentName={residentName}
      propertyName={property?.marketingName}
      unitNumber={unitNumberSpace}
      balanceDue={balanceDue}
      autopayEnabled={autopayEnabled}
      isConnection={isConnection}
      paymentsBlocked={paymentsBlocked}
    />
  );
};

export const PaymentsScreen: React.FC = () => {
  const { layoutMode } = useResponsive();

  // Mobile view
  if (layoutMode !== 'desktop') {
    return (
      <PaymentSummaryProvider>
        <MobilePaymentsContent />
      </PaymentSummaryProvider>
    );
  }

  // Desktop web rendering
  if (layoutMode === 'desktop') {
    return (
      <PaymentSummaryProvider>
        <ScrollView
          style={styles.desktopRoot}
          contentContainerStyle={styles.desktopContainer}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        >
          <ChargeSummaryLayout />
        </ScrollView>
      </PaymentSummaryProvider>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    flex: 1,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileHeaderSafeArea: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileHeaderTitleRow: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  mobileListContent: {
    paddingTop: 8,
    paddingBottom: 24,
  },
  mobileEmptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 80,
  },
  mobileEmptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  mobileEmptyStateMessage: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Desktop styles
  desktopRoot: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  desktopContainer: {
    paddingTop: 16,
    paddingBottom: 24,
  },
});